import { useEffect, useRef, useState, type FormEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useCan } from "../../auth/AuthProvider";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  Mono,
  Select,
  cx,
} from "../../components/ui";
import { QuotaAlert, quotaRefusal, type QuotaRefusal } from "../../components/Quota";
import { Icon } from "../../components/icons";
import { CopyButton } from "../../components/CopyButton";
import { ApiError } from "../../lib/api";
import { dnsRecordName, isApex, partnerHost } from "../../lib/domains";
import { relativeTime } from "../../lib/format";
import {
  useApp,
  useCreateDomain,
  useRegisteredDomains,
  useDeleteDomain,
  useDomains,
  useRecheckDomain,
} from "../../lib/queries";
import type { Domain, DomainVerificationStatus } from "../../lib/types";
import { useAppTabContext } from "./context";

/**
 * Connecting a domain, arranged around the one thing the old screen got wrong.
 *
 * It used to preview the DNS record inside the add form, driven by the input
 * field — so the values a customer needs were on screen only while they were
 * typing the hostname. Submitting cleared the input, and the domain they had
 * just added sat in the list saying "Waiting for DNS" with no record anywhere.
 * To see what to configure they had to retype the hostname into a form whose
 * button now rejected it as a duplicate.
 *
 * So the record moved onto the domain. A pending row carries its own type, name
 * and value, computed from its own host, and keeps carrying them until it goes
 * live. That is the whole redesign; everything else follows from it:
 *
 *   * **Adding is one line, not a three-step form.** There is exactly one
 *     required field. The steps existed to explain the DNS record, and the
 *     record now explains itself where it applies.
 *   * **Status is a dot, not a pill.** In a list the dot is what gets read, and
 *     it reads faster at the start of a row than a bordered label does at the
 *     end. Unsettled states pulse; a live domain is not doing anything.
 *   * **The row is the unit.** Status, record, diagnosis and actions sit
 *     together, so a domain that needs attention shows everything needed to fix
 *     it without opening anything.
 */

interface StatusLook {
  tone: string;
  /** Whether the state is still moving, and the dot should say so. */
  live: boolean;
}

/* The colour and the pulse; the word is `domains.status.<state>` in `app.json`. */
const STATUS: Record<DomainVerificationStatus, StatusLook> = {
  pending: { tone: "text-warn", live: true },
  active: { tone: "text-mint", live: false },
  failed: { tone: "text-danger", live: true },
};

/**
 * What the customer should do about this row, in a sentence.
 *
 * The label says which state a domain is in; this says whether that state is
 * theirs to act on. "Checking DNS" reads as a problem to somebody who has just
 * done everything right, and the honest answer is usually "nothing, wait". The
 * sentences are `domains.next.<state>`.
 */
function StatusDot({ status }: { status: DomainVerificationStatus }) {
  const look = STATUS[status];
  return (
    <span
      className={cx("dot-status", look.live && "dot-status-pulse", look.tone)}
      aria-hidden="true"
    />
  );
}

/**
 * The DNS record for one hostname, in the shape a provider's form asks for.
 *
 * A grid rather than a list, because the three values are read down as a unit
 * and copied one at a time — the labels are a column heading, not a prefix to
 * each line.
 */
function RecordCard({
  host,
  cnameTarget,
  apexIps,
  proxyAlreadyNamed = false,
}: {
  host: string;
  cnameTarget: string | null;
  apexIps: string[] | null;
  /**
   * Whether the row above has already told this customer their record is
   * proxied. The standing instruction stands down when it has — the same advice
   * twice in one row reads as a screen repeating itself rather than as emphasis.
   */
  proxyAlreadyNamed?: boolean;
}) {
  const { t } = useTranslation("app");
  const apex = isApex(host);
  const name = dnsRecordName(host);
  const values = apex ? (apexIps ?? []) : cnameTarget ? [cnameTarget] : [];

  return (
    <div className="overflow-hidden rounded-lg border border-line bg-canvas">
      <div className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-2.5 px-4 py-3.5 sm:grid-cols-[5rem_1fr]">
        <span className="micro self-center">{t("domains.record.type")}</span>
        <Mono className="text-xs text-fg">{apex ? "A" : "CNAME"}</Mono>

        <span className="micro self-center">{t("domains.record.name")}</span>
        <span className="flex min-w-0 items-center gap-2">
          <Mono className="truncate text-xs text-fg">{name}</Mono>
          <CopyButton value={name} label={t("domains.record.copyName")} />
        </span>

        <span className="micro self-start pt-0.5">{t("domains.record.value")}</span>
        <span className="min-w-0 space-y-1.5">
          {values.length === 0 ? (
            // Not an error state. The target is provisioned lazily, so the first
            // read of a brand-new app can genuinely arrive before it exists.
            <span className="text-xs text-fg-subtle">{t("domains.record.resolving")}</span>
          ) : (
            values.map((value) => (
              <span key={value} className="flex min-w-0 items-center gap-2">
                <Mono className="truncate text-xs text-fg">{value}</Mono>
                <CopyButton value={value} label={t("domains.record.copyValue", { value })} />
              </span>
            ))
          )}
        </span>
      </div>

      {/*
        The single highest-value sentence on this screen. Providers ask for the
        name relative to the zone, so pasting the whole hostname into a zone that
        already *is* that domain produces `app.example.com.example.com` — a record
        that exists, resolves, and is not the one anybody asked for. It is the
        most common way a first custom domain fails, and it fails silently.
      */}
      <div className="space-y-1.5 border-t border-line px-4 py-2.5 text-xs leading-5 text-fg-subtle">
        <p>
          {name === "@" ? t("domains.record.apex") : t("domains.record.justName", { name })}
          {apex && values.length > 1 && ` ${t("domains.record.onePerAddress")}`}
        </p>

        {/*
          The better apex record, where a provider offers one.
          
          An A record holds the address itself, so it breaks if the server's ever
          moves and the customer has to edit their own zone to fix it — whereas a
          subdomain's CNAME points at our record, which we re-point ourselves.
          ALIAS, ANAME and CNAME flattening are apex records that follow a name,
          which closes that gap entirely.
          
          Verification needs no special case for it: flattening resolves to the
          same origin addresses, so the IP sets still intersect.
        */}
        {/*
          The instruction that prevents the most common failure, placed with the
          values it applies to rather than in a banner somebody scrolls past.

          A proxied record resolves to the customer's own edge, so it never
          shares an address with our origin and verification can never match — it
          sits at "Checking DNS" indefinitely while looking, from their side,
          completely correct. It also puts their CDN between Let's Encrypt and
          the router, so the certificate cannot be issued.
        */}
        {!proxyAlreadyNamed && (
          <p>
            <Trans
              i18nKey="domains.record.unproxied"
              ns="app"
              components={{ em: <span className="text-fg-muted" /> }}
            />
          </p>
        )}

        {apex && cnameTarget && (
          <p>
            <Trans
              i18nKey="domains.record.alias"
              ns="app"
              values={{ target: cnameTarget }}
              components={{
                em: <span className="text-fg-muted" />,
                target: <Mono className="text-[11px] text-fg-muted" />,
              }}
            />
          </p>
        )}
      </div>
    </div>
  );
}

export function DomainsTab() {
  const { t } = useTranslation("app");
  const { orgId, appId } = useAppTabContext();
  const canEdit = useCan("developer");
  // Poll while a domain pends. The flag is state (not a ref) because the query
  // call below is where `domains` gets declared, so it cannot read this
  // render's data; the effect keeps the flag current for the next render.
  const [hasPending, setHasPending] = useState(false);
  const { data: domains, isPending, error } = useDomains(orgId, appId, hasPending);
  const attachedHosts = new Set((domains ?? []).map((d) => d.host));
  const { data: app } = useApp(orgId, appId);
  const createDomain = useCreateDomain(orgId ?? "", appId ?? "");
  const registered = useRegisteredDomains(orgId, { limit: 100, offset: 0 });
  const hostedNames = (registered.data?.data ?? [])
    .filter((row) => row.status === "active" && row.managedDns)
    .map((row) => row.domain);
  const deleteDomain = useDeleteDomain(orgId ?? "", appId ?? "");
  const recheckDomain = useRecheckDomain(orgId ?? "", appId ?? "");

  const [host, setHost] = useState("");
  /*
   * Where the name comes from. A name registered here is composed — a label
   * and one of the organization's own names — so there is nothing to type
   * wrong and nothing to configure afterwards; a name from elsewhere is typed
   * whole. Null until the customer picks: the default follows whether they
   * have a name here, which the list query knows and this render may not yet.
   */
  const [source, setSource] = useState<"here" | "elsewhere" | null>(null);
  const [label, setLabel] = useState("");
  const [parent, setParent] = useState<string | null>(null);
  const [path, setPath] = useState("/");
  // Blank rather than a literal 3000: sending a number the customer never
  // chose is how a domain ends up pointing somewhere the app does not listen.
  // Empty means "same as the app", which the API resolves from its own record.
  const [port, setPort] = useState("");
  const [certificateType, setCertificateType] = useState<"letsencrypt" | "none">("letsencrypt");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<QuotaRefusal | null>(null);
  // The row waiting for its second click before it is removed.
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  // A domain that flipped pending -> active while this tab was open.
  const [activated, setActivated] = useState<string | null>(null);

  const target = app?.defaultDomain ?? null;

  /*
   * What a custom domain gets pointed at — taken from the API rather than from
   * the default domain, because the two are not always the same.
   *
   * For an app named under its server the default domain is DNS-only and this
   * simply is it. For older apps the default domain is proxied at the edge, and
   * a hostname CNAMEd onto a proxied record arrives at Cloudflare naming a zone
   * that is not ours — which answers 1014, not the app. It verified anyway,
   * because both names resolved to the same edge addresses, so this used to go
   * green and then serve nothing; those apps carry a separate `.connect.` name.
   *
   * Either way `cnameTarget` resolves straight to the origin, so the router sees
   * the Host header and the certificate challenge can complete. Its IPs are the
   * origin's too, which is what an apex record needs — the old apex instructions
   * handed out Cloudflare's addresses, which are not ours to give.
   */
  const cnameTarget = app?.cnameTarget ?? null;
  const apexIps = app?.cnameTargetIps ?? null;

  const mode = source ?? (hostedNames.length > 0 ? "here" : "elsewhere");
  const parentName = parent && hostedNames.includes(parent) ? parent : (hostedNames[0] ?? null);
  // Somebody pasting the whole name into the label box is not wrong, only
  // early: the parent they already chose is taken off the end.
  const cleanLabel = label
    .trim()
    .toLowerCase()
    .replace(/^\.+|\.+$/g, "")
    .replace(parentName ? new RegExp(`(^|\\.)${parentName.replace(/\./g, "\\.")}$`) : /$^/, "");
  const typed =
    mode === "here" && parentName
      ? cleanLabel ? `${cleanLabel}.${parentName}` : parentName
      : host.trim().toLowerCase();
  // Only once it looks like a hostname. Validating on every keystroke would
  // reject "exa" while somebody is still typing "example.com".
  const looksLikeHost =
    /^[a-z0-9.-]+\.[a-z]{2,}$/.test(typed) &&
    (mode !== "here" || /^([a-z0-9-]+\.)*[a-z0-9-]*$/.test(cleanLabel)) &&
    !attachedHosts.has(typed);

  // Polling decides the refetch interval; this effect notices the flip the
  // poll brings back and says so, instead of the row silently going green.
  const prevStatuses = useRef(new Map<string, DomainVerificationStatus>());
  useEffect(() => {
    setHasPending((domains ?? []).some((d) => d.verificationStatus === "pending"));
    const prev = prevStatuses.current;
    for (const domain of domains ?? []) {
      if (prev.get(domain.id) === "pending" && domain.verificationStatus === "active") {
        setActivated(domain.host);
      }
      prev.set(domain.id, domain.verificationStatus);
    }
    for (const id of [...prev.keys()]) {
      if (!(domains ?? []).some((d) => d.id === id)) prev.delete(id);
    }
  }, [domains]);

  // A confirm-remove state without a second click reverts after three seconds.
  useEffect(() => {
    if (!confirmRemoveId) return;
    const timer = setTimeout(() => setConfirmRemoveId(null), 3000);
    return () => clearTimeout(timer);
  }, [confirmRemoveId]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setRefusal(null);
    try {
      await createDomain.mutateAsync({
        host: typed,
        path,
        ...(port.trim() ? { port: Number.parseInt(port, 10) } : {}),
        https: certificateType !== "none",
        certificateType,
      });
      setHost("");
      setLabel("");
      setPort("");
      setShowAdvanced(false);
    } catch (cause) {
      setRefusal(quotaRefusal(cause));
      setFormError(cause instanceof ApiError ? cause.message : t("domains.attachError"));
    }
  }

  function handleRemove(domain: Domain) {
    if (confirmRemoveId === domain.id) {
      setConfirmRemoveId(null);
      deleteDomain.mutate(domain.id);
    } else {
      setConfirmRemoveId(domain.id);
    }
  }

  const hasAnyDomain = (domains?.length ?? 0) > 0;
  const attached = attachedHosts;

  /**
   * Attaches the partner hostname with the settings its sibling already uses.
   *
   * Inheriting rather than defaulting matters: a customer who put their app on a
   * non-standard port, or turned HTTPS off, chose that for the pair and not for
   * one half of it.
   */
  async function addPartner(host: string, from: Domain) {
    setFormError(null);
    setRefusal(null);
    try {
      await createDomain.mutateAsync({
        host,
        path: from.path,
        port: from.port,
        https: from.certificateType !== "none",
        certificateType: from.certificateType === "none" ? "none" : "letsencrypt",
      });
    } catch (cause) {
      setFormError(cause instanceof ApiError ? cause.message : t("domains.addError", { host }));
    }
  }

  return (
    <div className="space-y-5">
      {activated && (
        <Alert tone="success">
          <span className="min-w-0 flex-1">
            <Trans
              i18nKey="domains.activated"
              ns="app"
              values={{ host: activated }}
              components={{ host: <Mono className="text-sm" /> }}
            />
          </span>
          <button
            type="button"
            aria-label={t("domains.dismiss")}
            onClick={() => setActivated(null)}
            className="grid size-6 shrink-0 place-items-center text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg"
          >
            ×
          </button>
        </Alert>
      )}

      {/* The app's own address, which needs no setup at all. First, because for
          most people it is already the answer and a custom domain is an extra
          rather than a prerequisite — but compact, because anybody on this tab
          has probably come looking for the other thing. */}
      {target && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-line bg-surface px-4 py-3">
          <span className="micro">{t("domains.thisApp")}</span>
          <a
            href={`https://${target}`}
            target="_blank"
            rel="noreferrer noopener"
            className="min-w-0 flex-1 break-all font-mono text-[13px] text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
          >
            {target}
          </a>
          <span className="flex shrink-0 items-center gap-2">
            <span className="hidden text-xs text-fg-subtle sm:inline">
              {t("domains.noSetup")}
            </span>
            <CopyButton value={`https://${target}`} label={t("domains.copyAppAddress")} />
          </span>
        </div>
      )}

      <Card>
        <CardHeader
          title={t("domains.title")}
          description={t("domains.lead")}
        />

        {/*
          One line, at the top, and the only required field.

          It used to be a three-step form below the list, and two of those steps
          were explanation: which domain, then a preview of the DNS record, then
          "we do the rest". The record now lives on the domain it belongs to, so
          what is left is the one thing this form ever needed to collect.
        */}
        {canEdit && (
          <form onSubmit={handleSubmit} className="border-b border-line px-4 py-3.5">
            {/*
              Two ways in, and the switch only appears once there are two. A
              name registered here is composed from a label and one of the
              organization's own names: the record is written for them and
              the certificate follows, so the form says so instead of the row
              later saying "waiting for DNS". A name from elsewhere is typed
              whole, and the record to add travels with the row as before.
            */}
            {hostedNames.length > 0 && (
              <div
                role="group"
                aria-label={t("domains.sourceLabel")}
                className="mb-3 inline-flex rounded-md border border-line p-0.5"
              >
                {(["here", "elsewhere"] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setSource(option)}
                    aria-pressed={mode === option}
                    className={cx(
                      "h-7 rounded px-2.5 text-xs font-medium transition-colors duration-200 ease-smooth",
                      mode === option ? "bg-raised text-fg" : "text-fg-subtle hover:text-fg",
                    )}
                  >
                    {t(option === "here" ? "domains.sourceHere" : "domains.sourceElsewhere")}
                  </button>
                ))}
              </div>
            )}

            {mode === "here" && parentName ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex min-w-0 flex-1 basis-72 items-center gap-1.5">
                    <Input
                      id="label"
                      placeholder="www"
                      autoComplete="off"
                      spellCheck={false}
                      aria-label={t("domains.labelField")}
                      className="min-w-0 flex-1 basis-24 font-mono"
                      value={label}
                      onChange={(event) => setLabel(event.target.value)}
                    />
                    <span className="text-sm text-fg-subtle" aria-hidden="true">
                      .
                    </span>
                    <div className="min-w-0 flex-[2] basis-40">
                      <Select
                        aria-label={t("domains.parentField")}
                        className="font-mono"
                        value={parentName}
                        onChange={(event) => setParent(event.target.value)}
                      >
                        {hostedNames.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <Button type="submit" loading={createDomain.isPending} disabled={!looksLikeHost}>
                    {t("domains.connect")}
                  </Button>
                </div>
                <p className="mt-2 text-xs leading-5 text-fg-subtle">
                  {attachedHosts.has(typed) ? (
                    t("domains.alreadyAttached", { host: typed })
                  ) : cleanLabel ? (
                    <Trans
                      i18nKey="domains.willConnect"
                      ns="app"
                      values={{ host: typed }}
                      components={{ host: <Mono className="text-fg" /> }}
                    />
                  ) : (
                    <Trans
                      i18nKey="domains.apexHint"
                      ns="app"
                      values={{ host: parentName }}
                      components={{ host: <Mono className="text-fg" /> }}
                    />
                  )}{" "}
                  {t("domains.noDnsNeeded")}
                </p>
              </>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id="host"
                    required
                    placeholder="www.example.com"
                    autoComplete="off"
                    spellCheck={false}
                    aria-label={t("domains.hostLabel")}
                    className="min-w-0 flex-1 basis-56"
                    value={host}
                    onChange={(event) => setHost(event.target.value)}
                  />
                  <Button type="submit" loading={createDomain.isPending} disabled={!looksLikeHost}>
                    {t("domains.connect")}
                  </Button>
                </div>
                {/*
                  Somebody with no name here is told, once and quietly, that
                  there is a way that skips the DNS step. Not while the list is
                  still loading: a nudge that appears and vanishes is noise.
                */}
                {hostedNames.length === 0 && registered.isSuccess && (
                  <p className="mt-2 text-xs leading-5 text-fg-subtle">
                    <Trans
                      i18nKey="domains.registerNudge"
                      ns="app"
                      components={{ a: <Link to="/domains/new" className="text-fg underline" /> }}
                    />
                  </p>
                )}
              </>
            )}

            <button
              type="button"
              onClick={() => setShowAdvanced((open) => !open)}
              className="mt-2.5 flex items-center gap-1.5 text-xs text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg"
            >
              <Icon
                name="chevron"
                className={cx(
                  "size-3.5 transition-transform duration-200 ease-smooth",
                  showAdvanced && "rotate-90",
                )}
              />
              {t("domains.advanced")}
            </button>

            {showAdvanced && (
              <div className="mt-3.5 grid gap-4 sm:grid-cols-3">
                <Field
                  label={t("domains.path")}
                  htmlFor="path"
                  hint={t("domains.pathHint")}
                >
                  <Input id="path" value={path} onChange={(event) => setPath(event.target.value)} />
                </Field>

                <Field
                  label={t("domains.port")}
                  htmlFor="domainPort"
                  hint={t("domains.portHint", { port: app?.port ?? 3000 })}
                >
                  <Input
                    id="domainPort"
                    type="number"
                    min={1}
                    max={65535}
                    placeholder={String(app?.port ?? 3000)}
                    value={port}
                    onChange={(event) => setPort(event.target.value)}
                  />
                </Field>

                <Field
                  label={t("domains.https")}
                  htmlFor="certificateType"
                  hint={t("domains.httpsHint")}
                >
                  <Select
                    id="certificateType"
                    value={certificateType}
                    onChange={(event) =>
                      setCertificateType(event.target.value as "letsencrypt" | "none")
                    }
                  >
                    <option value="letsencrypt">{t("domains.httpsOn")}</option>
                    <option value="none">{t("domains.httpsOff")}</option>
                  </Select>
                </Field>
              </div>
            )}

            {formError && (
              <div className="mt-3">
                {refusal ? <QuotaAlert refusal={refusal} /> : <Alert>{formError}</Alert>}
              </div>
            )}

            {!target && (
              <div className="mt-3">
                <Alert tone="warning">{t("domains.noTarget")}</Alert>
              </div>
            )}
          </form>
        )}

        {isPending ? (
          <LoadingBlock />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : !hasAnyDomain ? (
          <EmptyState
            icon="globe"
            title={t("domains.empty")}
            description={canEdit ? t("domains.emptyEdit") : t("domains.emptyView")}
          />
        ) : (
          <ul className="divide-y divide-line">
            {domains?.map((domain) => {
              const look = STATUS[domain.verificationStatus];
              const settled = domain.verificationStatus === "active";

              /*
               * What actually went wrong, when we know — the resolver's own
               * answer is more use than the generic advice, because "we found
               * 198.51.100.1 instead" tells somebody their record points at the
               * wrong thing rather than that it is missing.
               */
              /*
               * A proxied record beats every other diagnosis, including a
               * generic failure, because it is the one cause the customer cannot
               * see and would never guess. Their record exists, names the right
               * target and resolves — it just resolves to their own edge, so it
               * can never share an address with our origin.
               *
               * Checked on pending as well as failed: this is the state that
               * otherwise sits at "Checking DNS" until the 72-hour grace runs
               * out, and telling somebody on day one is the entire point.
               */
              const proxied = domain.resolvesToProxy === true && !settled;

              const diagnosis = proxied
                ? t("domains.proxied")
                : domain.verificationStatus === "failed" && domain.failureReason
                  ? domain.lastResolvedIps?.length
                    ? t("domains.found", { ips: domain.lastResolvedIps.join(", ") })
                    : domain.failureReason
                  : null;

              // Only when it is genuinely missing. Offering to add something
              // already in the list is a button that answers "already done".
              const candidate = partnerHost(domain.host);
              const partner =
                candidate && !attached.has(candidate) ? candidate : null;

              return (
                <li key={domain.id} className="px-4 py-3.5">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    {/*
                      A preferred width, so on a phone the buttons wrap under
                      the name instead of the name breaking every few letters
                      to leave them room on the same line.
                    */}
                    <span className={cx("flex min-w-0 flex-1 basis-64 items-center gap-2.5", look.tone)}>
                      <StatusDot status={domain.verificationStatus} />
                      {settled ? (
                        <a
                          href={domain.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="min-w-0 break-all font-mono text-[13px] text-fg transition-colors duration-200 ease-smooth hover:text-accent"
                        >
                          {domain.host}
                          {domain.path !== "/" ? domain.path : ""}
                        </a>
                      ) : (
                        <span className="min-w-0 break-all font-mono text-[13px] text-fg">
                          {domain.host}
                          {domain.path !== "/" ? domain.path : ""}
                        </span>
                      )}
                      <span className={cx("shrink-0 text-xs", look.tone)}>
                        {t(`domains.status.${domain.verificationStatus}` as "domains.status.active")}
                      </span>
                    </span>

                    <span className="ml-auto flex shrink-0 items-center gap-1">
                      {canEdit && !settled && (
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={recheckDomain.isPending && recheckDomain.variables === domain.id}
                          onClick={() => recheckDomain.mutate(domain.id)}
                        >
                          {t("domains.recheck")}
                        </Button>
                      )}
                      {canEdit && (
                        <Button
                          variant={confirmRemoveId === domain.id ? "danger" : "ghost"}
                          size="sm"
                          loading={deleteDomain.isPending && deleteDomain.variables === domain.id}
                          onClick={() => handleRemove(domain)}
                        >
                          {confirmRemoveId === domain.id
                            ? t("domains.confirmRemove")
                            : t("domains.remove")}
                        </Button>
                      )}
                    </span>
                  </div>

                  {/*
                    One sentence, not two.

                    The generic next step and a specific failure reason were both
                    rendered, and on a failed row they said the same thing twice
                    — "check the values, then check again" directly above "point a
                    CNAME at the app's address, then check again". The specific
                    one is strictly better when it exists, so it replaces rather
                    than joins.
                  */}
                  {diagnosis ? (
                    <p
                      className={cx(
                        "mt-1 pl-[1.375rem] text-xs leading-5",
                        // A proxied record is something to act on rather than a
                        // failure that has already happened, so it warns rather
                        // than alarms — but it must not read as "nothing to do".
                        proxied ? "text-warn" : "text-danger",
                      )}
                    >
                      {diagnosis}
                    </p>
                  ) : !settled && domain.hostedZone ? (
                    // Under a zone we host the next step is the same "nothing,
                    // wait", but said once, with why: the record is already in.
                    <p className="mt-1 flex items-start gap-1.5 pl-[1.375rem] text-xs leading-5 text-fg-muted">
                      <Icon name="check" className="mt-1 size-3.5 shrink-0 text-mint" />
                      <span>{t("domains.hostedZone")}</span>
                    </p>
                  ) : (
                    <p className="mt-1 pl-[1.375rem] text-xs leading-5 text-fg-subtle">
                      {t(`domains.next.${domain.verificationStatus}` as "domains.next.active")}
                    </p>
                  )}

                  {/*
                    The record, on the domain that needs it.

                    This is the fix the whole screen was rearranged around: the
                    values used to exist only as a preview inside the add form,
                    so they vanished the moment the domain was created and the
                    only way back to them was retyping a hostname the form then
                    rejected as a duplicate.
                  */}
                  {!settled && !domain.hostedZone && (
                    <div className="mt-3 pl-[1.375rem]">
                      <RecordCard
                        host={domain.host}
                        cnameTarget={cnameTarget}
                        apexIps={apexIps}
                        proxyAlreadyNamed={proxied}
                      />
                    </div>
                  )}

                  {/*
                    The other half of a www pair.

                    Almost nobody wants only one of `example.com` and
                    `www.example.com` — a visitor types whichever they remember,
                    and the one that was never attached simply fails. Offered on
                    the row rather than in the form because this is the moment it
                    becomes obvious, and attaching it is the same operation that
                    was just performed.

                    It *adds* the partner; it does not redirect to it. Both end
                    up serving the app. A real 301 needs a Traefik middleware and
                    the deployment engine's domain API has no redirect to ask for,
                    so the label says "also add" rather than implying otherwise.
                  */}
                  {canEdit && partner && (
                    <button
                      type="button"
                      onClick={() => void addPartner(partner, domain)}
                      disabled={createDomain.isPending}
                      className={cx(
                        "mt-2 ml-[1.375rem] inline-flex items-center gap-1.5 rounded-md border border-line px-2 py-1",
                        "text-xs text-fg-muted transition-colors duration-200 ease-smooth",
                        "hover:border-line-strong hover:text-fg disabled:opacity-50",
                      )}
                    >
                      <Icon name="plus" className="size-3" />
                      {t("domains.alsoAdd")} <Mono className="text-[11px]">{partner}</Mono>
                    </button>
                  )}

                  {/* The mechanics, kept quiet: true, occasionally needed, and
                      not what anyone is looking for on this screen. */}
                  <p className="mt-2 pl-[1.375rem] text-xs text-fg-subtle">
                    {t("domains.mechanics", {
                      at: relativeTime(domain.createdAt),
                      port: domain.port,
                      certificate:
                        domain.certificateType === "letsencrypt"
                          ? t("domains.certificate")
                          : t("domains.noCertificate"),
                    })}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
